import { useState } from 'react';

import { appColors, lottieAnimations } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { ResourceNugget } from '@backrs_ui/grow-ui/src/components/Lubav/nuggets/ResourceNugget';
import { ToolNugget } from '@backrs_ui/grow-ui/src/components/Lubav/nuggets/ToolNugget';
import {
  ResourcesContent,
  RESOURCE_KEY,
  ResourceKey,
  LubavResource,
  LubavTool,
} from '@backrs_ui/grow-ui/src/models/models';
import { selectResourcesContent } from '@backrs_ui/grow-ui/src/redux/boxData/boxData';
import { useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import {
  getActiveLubavSession,
  getSessionState,
} from '@backrs_ui/grow-ui/src/redux/lubavSessionState/lubavSessionState.utils';

import { Box, Theme, SxProps, Button } from '@mui/material';
import Lottie from 'lottie-react';

const { officeHours } = appStrings.modules;

const LOADING_TEXT_HEIGHT = 24;

const BOX_ORDER = ['TOOLS', 'RESOURCES', 'CONNECTIONS', 'OPPORTUNITIES'] as const;

type BoxType = (typeof BOX_ORDER)[number];

type BoxConfig = Readonly<{
  title: string;
  resources: ResourceKey[];
}>;

const INFO_BOX_CONFIG: Record<BoxType, BoxConfig> = {
  TOOLS: {
    title: officeHours.categories.tools,
    resources: [
      RESOURCE_KEY.TOOLS,
      RESOURCE_KEY.CAREER_PATHS,
      RESOURCE_KEY.COLLEGES,
      RESOURCE_KEY.COMPETENCIES,
      // RESOURCE_KEY.EMPLOYERS,
      // RESOURCE_KEY.NETWORKS,
    ],
  },
  RESOURCES: {
    title: officeHours.categories.resources,
    resources: [RESOURCE_KEY.LEARNING, RESOURCE_KEY.STORIES],
  },
  CONNECTIONS: {
    title: officeHours.categories.connections,
    resources: [RESOURCE_KEY.BACKRS, RESOURCE_KEY.STUDENTS, RESOURCE_KEY.PROVIDERS],
  },
  OPPORTUNITIES: {
    title: officeHours.categories.opportunities,
    resources: [RESOURCE_KEY.INTERNSHIPS, RESOURCE_KEY.SCHOLARSHIPS],
  },
};

type ResourceItemProps = Readonly<{
  responseData: ResourcesContent;
  resourceKey: ResourceKey;
}>;

const ResourceItem = ({ responseData, resourceKey }: ResourceItemProps) => {
  const content = responseData?.[resourceKey]?.content;

  if (!content || content.length === 0) {
    return null;
  }

  switch (resourceKey) {
    case RESOURCE_KEY.TOOLS:
      return (
        <Box sx={resourcesContainerStyles}>
          {(content as LubavTool[]).map((tool) => (
            <ToolNugget key={tool.id} tool={tool} />
          ))}
        </Box>
      );
    default:
      return (
        <Box sx={resourcesContainerStyles}>
          {(content as LubavResource[]).map((resource) => (
            <ResourceNugget key={resource.id} type={resourceKey} resource={resource} />
          ))}
        </Box>
      );
  }
};

export const InfoBox = () => {
  const activeSession = useAppSelector(getActiveLubavSession);

  const { loading: isLoadingResources } = useAppSelector(
    (state) => getSessionState(state, activeSession).boxData,
  );

  const resourcesContent = useAppSelector((state) => selectResourcesContent(state, activeSession));

  const hasContent = (boxType: BoxType) =>
    INFO_BOX_CONFIG[boxType].resources.some(
      (resourceKey) => (resourcesContent?.[resourceKey]?.content?.length ?? 0) > 0,
    );

  const availableBoxTypes = BOX_ORDER.filter(hasContent);
  const [selectedBoxType, setSelectedBoxType] = useState<BoxType>(
    availableBoxTypes[0] || BOX_ORDER[0],
  );

  if (!activeSession) {
    return null;
  }

  if (isLoadingResources) {
    return (
      <Box sx={loaderStyles}>
        <Box sx={animationStyles}>
          <Lottie animationData={lottieAnimations.dotsDotting} loop width={250} height={250} />
        </Box>
        <Box sx={loadingTextStyles}>
          {officeHours.loadingMessages.map((message, index) => (
            <Box component="span" key={index} sx={loadingMessageStyles}>
              {message}
            </Box>
          ))}
        </Box>
      </Box>
    );
  }

  if (availableBoxTypes.length === 0) {
    return null;
  }

  return (
    <Box sx={containerStyles}>
      <Box sx={chipContainerStyles}>
        {availableBoxTypes.map((boxType) => (
          <Button
            key={boxType}
            hierarchy="Primary"
            size="md"
            onClick={() => setSelectedBoxType(boxType)}
            sx={{
              backgroundColor: selectedBoxType === boxType ? undefined : appColors.color.blue10,
              color: selectedBoxType === boxType ? undefined : appColors.neutral.black,
            }}
          >
            {INFO_BOX_CONFIG[boxType].title}
          </Button>
        ))}
      </Box>
      <Box sx={contentContainerStyles}>
        {(() => {
          const currentBox = INFO_BOX_CONFIG[selectedBoxType];
          const availableResources = currentBox.resources.filter(
            (resourceKey) => (resourcesContent?.[resourceKey]?.content?.length ?? 0) > 0,
          );

          // sort by relevance score
          const sortedContent = availableResources
            .flatMap((resourceKey) =>
              (resourcesContent?.[resourceKey]?.content || []).map((item) => ({
                resourceKey,
                item,
                relevanceScore: item.metadata?.relevance_score || 0,
              })),
            )
            .sort((a, b) => b.relevanceScore - a.relevanceScore);

          return (
            <Box sx={resourcesContainerStyles}>
              {sortedContent.map((content, index) => (
                <Box key={`${content.resourceKey}-${index}`} sx={resourceItemContainerStyles}>
                  {resourcesContent && (
                    <ResourceItem
                      responseData={
                        {
                          [content.resourceKey]: {
                            content: [content.item],
                          },
                        } as ResourcesContent
                      }
                      resourceKey={content.resourceKey}
                    />
                  )}
                </Box>
              ))}
            </Box>
          );
        })()}
      </Box>
    </Box>
  );
};

const containerStyles: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  height: 'calc(100vh - 104px)',
  padding: '0px 0px 0px 60px',
  width: '100%',
  '@media (max-width: 1015px)': {
    height: 'calc(100vh - 235px)',
  },
  '@media (max-width: 900px)': {
    padding: '20px 0px 0px 0px',
    height: '60%',
    width: 'calc(100vw - 160px)',
  },
} as const;

const contentContainerStyles: SxProps<Theme> = {
  overflow: 'auto',
  height: '100%',
  flexGrow: 1,
  display: 'flex',
  flexDirection: 'column',
} as const;

const loaderStyles: SxProps<Theme> = {
  width: '100%',
  margin: 'auto',
  paddingTop: 'calc(40vh - 25%)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  paddingLeft: '34px',
  paddingRight: '34px',
} as const;

const chipContainerStyles: SxProps<Theme> = {
  display: 'flex',
  gap: '12px',
  marginBottom: '20px',
  flexWrap: 'wrap',
  width: '100%',
} as const;

const animationStyles: SxProps<Theme> = {
  margin: '0 auto',
} as const;

const loadingTextStyles: SxProps<Theme> = {
  textAlign: 'center',
  color: appColors.color.blue1,
  fontSize: '18px',
  fontWeight: 600,
  marginTop: '20px',
  height: `${LOADING_TEXT_HEIGHT}px`,
  width: '100%',
  overflow: 'hidden',
} as const;

const loadingMessageStyles: SxProps<Theme> = {
  display: 'block',
  whiteSpace: 'nowrap',
  animation: 'cycleMessages 10s infinite',
  lineHeight: `${LOADING_TEXT_HEIGHT}px`,
  width: '100%',
  '@keyframes cycleMessages': {
    '0%, 20%': { transform: 'translateY(0)' },
    '25%, 45%': { transform: `translateY(-${LOADING_TEXT_HEIGHT}px)` },
    '50%, 70%': { transform: `translateY(-${LOADING_TEXT_HEIGHT * 2}px)` },
    '75%, 95%': { transform: `translateY(-${LOADING_TEXT_HEIGHT * 3}px)` },
    '100%': { transform: `translateY(-${LOADING_TEXT_HEIGHT * 4}px)` },
  },
} as const;

const resourcesContainerStyles: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
} as const;

const resourceItemContainerStyles: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
} as const;
